import { UzButton } from '@utazzo/ui';
import { FileDown, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { resolveSelectedCodes } from '../cost-selection';
import { LEAD_TYPE_LABEL, useMarketingLeadType } from '../lead-type';
import { monthLabel, serialiseMonths } from '../month-selection';
import {
  useMarketingActiveStatus,
  useMarketingCampaignContactability,
  useMarketingChannels,
  useMarketingContactability,
  useMarketingCostHeads,
  useMarketingDestinations,
  useMarketingLeadStatus,
  useMarketingQueryProfit,
  useMarketingSpend,
} from '../use-marketing-reports';

import { ActiveStatusTable } from './ActiveStatusTable';
import { CampaignContactabilityTable } from './CampaignContactabilityTable';
import { ContactabilityTables } from './ContactabilityTables';
import {
  DestinationContactability,
  DestinationLostReasons,
  DestinationProfitability,
  DestinationTimeline,
} from './DestinationsTable';
import { ExcelProfitSheet } from './ExcelProfitSheet';
import { ActiveRatioTable, LeadStatusTable } from './LeadStatusTable';
import { ForceExpandContext } from './month-table';
import { QueryChannelTable } from './QueryChannelTable';

import { ReportSurfaceContext } from '@/features/reports/components/ReportSection';
import { exportTablesAsExcel } from '@/features/reports/reports-excel';

/** Full-screen scroll: no card chrome, and no per-card expand button in it. */
const SHEET_SURFACE = { surface: 'sheet', expandable: false } as const;

/**
 * The whole Marketing report on one full-screen scroll.
 *
 * One button, one scroll, nothing to switch: every table is on screen in order,
 * and reading the report end to end is a scroll rather than a round of clicks.
 * An earlier draft put the tables behind spreadsheet-style sheet tabs; the tabs
 * were the thing to get rid of, not to rebuild in another shape.
 *
 * Every read fires when it opens and nothing is deferred, because on this screen
 * everything is about to be looked at. The page behind shares the same cache, so
 * opening this after reading the page costs nothing.
 *
 * Deliberately not a route: it is a way of LOOKING at the report that is open
 * for a few minutes during the monthly review, not a place to land on, and the
 * month and cost pickers that scope it live on the page underneath.
 */
export const MarketingWorkbook = ({
  months,
  costs,
  canEdit,
  canExport,
  onClose,
}: {
  months: string[];
  costs: string[] | null;
  canEdit: boolean;
  canExport: boolean;
  onClose: () => void;
}): React.JSX.Element => {
  // Everything at once. That IS the feature - a workbook whose sheets each
  // needed loading would be the tabs it is meant to replace.
  const profit = useMarketingQueryProfit(months, costs);
  // The section open on the page behind. Organic has no cost side.
  const leadType = useMarketingLeadType();
  const organic = leadType === 'organic';
  // Organic has no cost side - and its readers may not fetch one.
  const spend = useMarketingSpend(organic ? null : months, costs);
  const channels = useMarketingChannels(months);
  const destinations = useMarketingDestinations(months);
  const campaigns = useMarketingCampaignContactability(months);
  const leadStatus = useMarketingLeadStatus(months);
  const activeStatus = useMarketingActiveStatus(months);
  const contactability = useMarketingContactability(months);
  const costHeads = useMarketingCostHeads(!organic);

  const sections = [
    {
      key: 'profit',
      name: 'Profit & Spend',
      body: (
        <ExcelProfitSheet
          profit={profit.data}
          spend={spend.data}
          loading={profit.isLoading || spend.isLoading}
          canEdit={canEdit}
        />
      ),
    },
    // From here down: the order of the client's workbook, block for block. The
    // Overview page runs in the same order (SECTIONS in MarketingOverview) and
    // the Excel file is read off this screen, so all three stay in step.
    {
      key: 'active-ratio',
      name: 'Query to Active Ratio',
      body: <ActiveRatioTable month={profit.data?.months[0]} loading={profit.isLoading} />,
    },
    {
      key: 'contactability',
      name: 'Region & Agent',
      body: (
        <div className="space-y-8">
          <ContactabilityTables data={contactability.data} loading={contactability.isLoading} />
        </div>
      ),
    },
    {
      key: 'dest-contact',
      name: 'Destination Contactability',
      body: <DestinationContactability data={destinations.data} loading={destinations.isLoading} />,
    },
    {
      key: 'active-status',
      name: 'Active Status',
      body: <ActiveStatusTable data={activeStatus.data} loading={activeStatus.isLoading} />,
    },
    {
      key: 'dest-timeline',
      name: 'Booking Timeline',
      body: <DestinationTimeline data={destinations.data} loading={destinations.isLoading} />,
    },
    {
      key: 'campaigns',
      name: 'Campaigns',
      body: <CampaignContactabilityTable data={campaigns.data} loading={campaigns.isLoading} />,
    },
    {
      key: 'dest-lost',
      name: 'Lost Reasons',
      body: <DestinationLostReasons data={destinations.data} loading={destinations.isLoading} />,
    },
    {
      key: 'dest-profit',
      name: 'Destination Profitability',
      body: <DestinationProfitability data={destinations.data} loading={destinations.isLoading} />,
    },
    // Not on the client's sheet, so after everything that is.
    {
      key: 'channels',
      name: 'Query Channel',
      body: <QueryChannelTable channels={channels.data} loading={channels.isLoading} />,
    },
    {
      key: 'lead-status',
      name: 'Lead Status',
      body: (
        <LeadStatusTable
          status={leadStatus.data}
          loading={leadStatus.isLoading}
          showSummary={false}
        />
      ),
    },
  ];

  // Everything on this screen goes into the file - the whole scroll, in order,
  // with the filters already applied to what is rendered.
  const scrollRef = useRef<HTMLDivElement>(null);
  /**
   * Every group is forced open for the moment of the export.
   *
   * The file is read off the rendered tables, and the sheet opens folded, so
   * exporting as-is would hand over band headings with no detail underneath -
   * which is the one thing nobody wants out of a spreadsheet. Two frames are
   * awaited so React has actually painted the expanded rows before they are
   * read, and the screen goes back to whatever the operator had open.
   */
  const [exporting, setExporting] = useState(false);
  const onExport = async (): Promise<void> => {
    if (!scrollRef.current) return;
    setExporting(true);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    try {
      const exported = await exportTablesAsExcel(
        scrollRef.current,
        `marketing-report-${leadType}-${serialiseMonths(months)}.xlsx`,
        'Marketing Report',
      );
      if (!exported) toast.error('Nothing to export yet - the tables are still loading.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    // Both the document element AND the body, because the window's own
    // scrollbar sits OUTSIDE the viewport: a `fixed inset-0` overlay cannot
    // cover it, so the page behind would keep showing a second scrollbar down
    // the right edge next to this screen's own. Locking `body` alone does not
    // do it - the scroll lives on the document element.
    const root = document.documentElement;
    const previous = { root: root.style.overflow, body: document.body.style.overflow };
    root.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      root.style.overflow = previous.root;
      document.body.style.overflow = previous.body;
    };
  }, [onClose]);

  const range =
    months.length === 0
      ? ''
      : months.length === 1
        ? monthLabel(months[0]!)
        : `${monthLabel(months[0]!)} - ${monthLabel(months[months.length - 1]!)}`;

  // Which costs are in these figures, named. A Total Spend that quietly leaves
  // a head out is the easiest number on this screen to misread, so the header
  // says what is in it rather than making the reader open the cost picker.
  const activeHeads = (costHeads.data?.data ?? []).filter((head) => head.isActive);
  const includedCodes = new Set(
    resolveSelectedCodes(
      costs,
      activeHeads.map((head) => head.code),
    ),
  );
  const includedNames = activeHeads
    .filter((head) => includedCodes.has(head.code))
    .map((head) => head.name);

  return (
    <div className="bg-background fixed inset-0 z-70 flex flex-col">
      <header className="border-border bg-card flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <div className="min-w-0">
          <h2 className="text-foreground truncate text-lg font-semibold">
            Marketing Report · {LEAD_TYPE_LABEL[leadType]}
          </h2>
          <p className="text-muted-foreground text-sm">{range} · every table, one scroll</p>
        </div>

        {/* The cost side sits on the right, where the header is otherwise
            empty, rather than under the title where it pushed the report down. */}
        <div className="flex items-center gap-3">
          {!organic && (
            <div className="flex flex-wrap items-center justify-end gap-1 text-xs">
              <span className="text-muted-foreground">Costs in these figures:</span>
              <span className="border-border text-foreground rounded border px-1.5 py-0.5">
                Ad spend
              </span>
              {includedNames.map((name) => (
                <span
                  key={name}
                  className="border-border text-foreground rounded border px-1.5 py-0.5"
                >
                  {name}
                </span>
              ))}
              {includedNames.length === 0 && (
                <span className="text-muted-foreground">no cost heads ticked</span>
              )}
            </div>
          )}
          {canExport && (
            <UzButton
              variant="outline"
              size="sm"
              onClick={() => void onExport()}
              title="Download this screen as an Excel file"
            >
              <FileDown className="mr-1.5 h-4 w-4" />
              Export Excel
            </UzButton>
          )}
          <UzButton variant="outline" size="sm" onClick={onClose} title="Close (Esc)">
            <X className="mr-1.5 h-4 w-4" />
            Close
          </UzButton>
        </div>
      </header>

      {/* One scroll for the whole report. The padding is on the INNER wrapper,
          never on the scroll box: a scroll container with its own padding pins
          the sticky top and left cells to the content-box edge, which leaves the
          padding as a gap above each frozen header where the scrolled-away rows
          show through it. */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
        <div className="space-y-8 px-5 py-4">
          <ReportSurfaceContext.Provider value={SHEET_SURFACE}>
            <ForceExpandContext.Provider value={exporting}>
              {sections.map((section) => (
                <div key={section.key}>{section.body}</div>
              ))}
            </ForceExpandContext.Provider>
          </ReportSurfaceContext.Provider>
        </div>
      </div>
    </div>
  );
};
